import { AxiosResponse } from 'axios';
import { apiClient } from '@/lib/api-client';
import { getActiveProjectId } from '@/shared/api/active-project';
import { ensureCsrfHeader } from '@/utils/csrf';
import { getErrorMessage, normalizeError } from '@/shared/errors/app-error';

const csrfRequired = (method: string): boolean =>
  ['post', 'put', 'patch', 'delete'].includes(method.toLowerCase());

// Appends `project_id=<activeProjectId>` to the endpoint when the caller is on
// a project-scoped page and didn't already specify one. Preserves any existing
// query string and hash fragment. Absolute URLs are handled too, since axios
// accepts them and we only touch the search params.
const withProjectId = (endpoint: string): string => {
  const projectId = getActiveProjectId();
  if (!projectId) return endpoint;

  const [pathAndQuery, hash = ''] = endpoint.split('#');
  const [path, query = ''] = pathAndQuery.split('?');

  const params = new URLSearchParams(query);
  if (params.has('project_id')) return endpoint;
  params.set('project_id', projectId);

  const rebuilt = `${path}?${params.toString()}`;
  return hash ? `${rebuilt}#${hash}` : rebuilt;
};

export const request = async <T>(
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  endpoint: string,
  payload?: T,
  headers?: Record<string, string>,
  signal?: AbortSignal,
): Promise<AxiosResponse> => {
  try {
    // Handle CSRF headers for methods that require it
    const baseHeaders = csrfRequired(method) ? await ensureCsrfHeader(headers) : headers;

    // Handle FormData content type
    const isFormData = payload instanceof FormData;
    const finalHeaders = {
      ...baseHeaders,
      ...(isFormData ? { 'Content-Type': undefined } : {}),
    };

    const config = { headers: finalHeaders, signal };
    const finalEndpoint = withProjectId(endpoint);

    const response =
      method === 'get' || method === 'delete'
        ? await apiClient[method](finalEndpoint, config)
        : await apiClient[method]<T>(finalEndpoint, payload, config);

    return response;
  } catch (error) {
    throw normalizeError(error);
  }
};

export const GET = async <T>(endpoint: string, headers?: Record<string, string>) =>
  await request<T>('get', endpoint, undefined, headers);

export const POST = async <T>(endpoint: string, payload: T, headers?: Record<string, string>) =>
  await request<T>('post', endpoint, payload, headers);

export const PUT = async <T>(endpoint: string, payload: T, headers?: Record<string, string>) =>
  await request<T>('put', endpoint, payload, headers);

export const PATCH = async <T>(endpoint: string, payload: T, headers?: Record<string, string>) =>
  await request<T>('patch', endpoint, payload, headers);

export const DELETE = async <T>(endpoint: string, headers?: Record<string, string>) =>
  await request<T>('delete', endpoint, undefined, headers);

// Error handling helper
export const handleApiError = (error: unknown): string => getErrorMessage(error);
